import { useState, useMemo, useCallback, memo } from "react";
import { Search, Filter, X } from "lucide-react";
import { HotelFilterModal } from "./HotelFilterModal";

interface HotelListFiltersProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  city: string;
  setCity: (city: string) => void;
  state: string;
  setState: (state: string) => void;
  status: string;
  setStatus: (status: string) => void;
  salesmanCode: string;
  setSalesmanCode: (code: string) => void;
}

export const HotelListFilters = memo(
  ({
    searchQuery,
    setSearchQuery,
    city,
    setCity,
    state,
    setState,
    status,
    setStatus,
    salesmanCode,
    setSalesmanCode,
  }: HotelListFiltersProps) => {
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    const clearFilters = useCallback(() => {
      setSearchQuery("");
      setCity("");
      setState("");
      setStatus("all");
      setSalesmanCode("");
    }, [setSearchQuery, setCity, setState, setStatus, setSalesmanCode]);

    const activeFiltersCount = useMemo(() => {
      return [
        city,
        state,
        status !== "all" ? status : null,
        salesmanCode,
      ].filter(Boolean).length;
    }, [city, state, status, salesmanCode]);

    const hasActiveFilters = useMemo(() => {
      return searchQuery || activeFiltersCount > 0;
    }, [searchQuery, activeFiltersCount]);

    const handleApplyFilters = useCallback(
      ({ city, state, status, salesmanCode }: any) => {
        setCity(city);
        setState(state);
        setStatus(status);
        setSalesmanCode(salesmanCode);
      },
      [setCity, setState, setStatus, setSalesmanCode],
    );

    return (
      <div className="bg-white dark:bg-[#111c44] p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
        {/* Search Bar */}
        <div className="flex-1 relative">
          <Search
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500"
            size={18}
          />
          <input
            type="text"
            placeholder="Search by Name, Email, or Phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 h-11 bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-white/10 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-500 font-medium text-slate-700 dark:text-slate-200"
          />
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
          <button
            onClick={() => setIsFilterModalOpen(true)}
            className={`h-11 px-5 rounded-xl flex items-center justify-center gap-2 transition-all border text-sm font-semibold shadow-sm ${
              activeFiltersCount > 0
                ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40"
                : "bg-white dark:bg-[#111c44] border-slate-200 dark:border-white/10 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#1b254b] hover:border-slate-300 dark:hover:border-white/20"
            }`}
          >
            <Filter
              size={16}
              className={
                activeFiltersCount > 0
                  ? "fill-blue-700/20 dark:fill-blue-400/20"
                  : ""
              }
            />
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <span className="bg-blue-600 dark:bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1 min-w-[18px] text-center">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="h-11 w-11 rounded-xl flex items-center justify-center text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 border border-slate-200 dark:border-white/10 hover:border-red-200 dark:hover:border-red-800 transition-all shadow-sm bg-white dark:bg-[#111c44]"
              title="Clear All Filters"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <HotelFilterModal
          isOpen={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          currentCity={city}
          currentState={state}
          currentStatus={status}
          currentSalesmanCode={salesmanCode}
          onApply={handleApplyFilters}
          onClear={clearFilters}
        />
      </div>
    );
  },
);
